import { EmitType } from '@syncfusion/ej2-base';
import { GridActionEventArgs, GridComponent } from '@syncfusion/ej2-react-grids';
import { RefObject } from 'react';
import { IJournalEntryGridProps } from '../components/sidebar/IJournalEntryGridProps';
import { IJournalEntryPendingArgs } from '../components/sidebar/IJournalEntryPendingArgs';
import { IEnterAmountsDialogDatasetFilterSelections } from '../hooks/useEnterAmountsData';
import { IWhitePaperReportChanges } from '../model';

export enum ConfirmationDialogType {
    FilterReset = 'FilterReset',
    UnsavedChanges = 'UnsavedChanges',
}

export interface IEnterAmountsDialogState {
    location: string;
    show: boolean;
    changeTrackingData: IWhitePaperReportChanges;
    saveButtonIsDisabled: boolean;
    rowDescription: string;
    amountsOnly: boolean;
    combineDebitCreditColumns: boolean;
    showAllAccounts: boolean;
    applyButtonDisabled: boolean;
    journalEntryGridProps: IJournalEntryGridProps | null;
    currentFilterSelections: IEnterAmountsDialogDatasetFilterSelections;
    datasetFilterSelections: IEnterAmountsDialogDatasetFilterSelections;
    showConfirmationDialog: boolean;
    confirmationDialogType: ConfirmationDialogType | null;
    isDirty: boolean;
    isFilterReset: boolean;
    pendingJournalEntryArgs?: IJournalEntryPendingArgs | null;
}
export enum EnterAmountsModalActionActionType {
    CellEdited,
    FiltersUpdated,
    FiltersApplied,
    FiltersReset,
    AmountsSaved,
    JournalEntryOpened,
    JournalEntryClosed,
    ConfirmationDialogOpened,
    ConfirmationDialogClosed,
}

export type EnterAmountsDialogReducer = (
    state: IEnterAmountsDialogState,
    action: EnterAmountsDialogReducerAction
) => IEnterAmountsDialogState;

export type EnterAmountsDialogReducerAction =
    | { type: EnterAmountsModalActionActionType.CellEdited }
    | {
          type: EnterAmountsModalActionActionType.FiltersUpdated;
          filterSelections: Partial<IEnterAmountsDialogState>;
      }
    | { type: EnterAmountsModalActionActionType.AmountsSaved; changeTrackingData: IWhitePaperReportChanges }
    | {
          type: EnterAmountsModalActionActionType.JournalEntryOpened;
          accountCode: string;
          gridActionComplete: EmitType<GridActionEventArgs>;
          gridRef: RefObject<GridComponent>;
      }
    | { type: EnterAmountsModalActionActionType.JournalEntryClosed }
    | { type: EnterAmountsModalActionActionType.FiltersApplied }
    | {
          type: EnterAmountsModalActionActionType.ConfirmationDialogOpened;
          dialogType: ConfirmationDialogType;
          journalEntryArgs?: IJournalEntryPendingArgs;
      }
    | { type: EnterAmountsModalActionActionType.ConfirmationDialogClosed }
    | { type: EnterAmountsModalActionActionType.FiltersReset };

export function enterAmountsDialogStateReducer(
    state: IEnterAmountsDialogState,
    action: EnterAmountsDialogReducerAction
): IEnterAmountsDialogState {
    switch (action.type) {
        case EnterAmountsModalActionActionType.CellEdited:
            return { ...state, saveButtonIsDisabled: false, isDirty: true };
        case EnterAmountsModalActionActionType.FiltersUpdated: {
            const isInitialFilterLoad =
                !state.datasetFilterSelections.adjustment &&
                !state.datasetFilterSelections.case &&
                !state.datasetFilterSelections.entity &&
                !state.datasetFilterSelections.jurisdiction &&
                !state.datasetFilterSelections.calendarPeriod;
            const isDatasetChange =
                !isInitialFilterLoad &&
                action.filterSelections.datasetFilterSelections &&
                (state.datasetFilterSelections.adjustment?.adjustmentCode !==
                    action.filterSelections.currentFilterSelections?.adjustment?.adjustmentCode ||
                    state.datasetFilterSelections.case?.code !== action.filterSelections.currentFilterSelections?.case?.code ||
                    state.datasetFilterSelections.entity?.code !== action.filterSelections.currentFilterSelections?.entity?.code ||
                    state.datasetFilterSelections.jurisdiction?.code !==
                        action.filterSelections.currentFilterSelections?.jurisdiction?.code ||
                    state.datasetFilterSelections.calendarPeriod?.key !==
                        action.filterSelections.currentFilterSelections?.calendarPeriod?.key);
            return {
                ...state,
                applyButtonDisabled: isDatasetChange ? false : state.applyButtonDisabled,
                amountsOnly: action?.filterSelections?.amountsOnly ?? state.amountsOnly,
                combineDebitCreditColumns: action?.filterSelections?.combineDebitCreditColumns ?? state.combineDebitCreditColumns,
                currentFilterSelections: action.filterSelections.currentFilterSelections ?? state.currentFilterSelections,
                datasetFilterSelections: action.filterSelections.datasetFilterSelections ?? state.datasetFilterSelections,
                showAllAccounts: action?.filterSelections?.showAllAccounts ?? state.showAllAccounts,
            };
        }
        case EnterAmountsModalActionActionType.AmountsSaved:
            return { ...state, saveButtonIsDisabled: true, changeTrackingData: action.changeTrackingData, isDirty: false };
        case EnterAmountsModalActionActionType.JournalEntryOpened:
            return {
                ...state,
                journalEntryGridProps: {
                    accountCode: action.accountCode,
                    adjustmentCode: state.datasetFilterSelections.adjustment?.adjustmentCode ?? '',
                    caseCode: state.datasetFilterSelections.case?.code ?? '',
                    entityCode: state.datasetFilterSelections.entity?.code ?? '',
                    jurisdiction: state.datasetFilterSelections.jurisdiction?.code ?? '',
                    gridActionComplete: action.gridActionComplete,
                    gridRef: action.gridRef,
                    period: state.datasetFilterSelections.calendarPeriod?.periodYear?.toString() ?? '',
                },
                pendingJournalEntryArgs: null,
            };
        case EnterAmountsModalActionActionType.JournalEntryClosed:
            return { ...state, journalEntryGridProps: null, pendingJournalEntryArgs: null };
        case EnterAmountsModalActionActionType.FiltersApplied:
            return {
                ...state,
                saveButtonIsDisabled: true,
                applyButtonDisabled: true,
                datasetFilterSelections: state.currentFilterSelections!,
                showConfirmationDialog: false,
                confirmationDialogType: null,
                isDirty: false,
                isFilterReset: false,
            };
        case EnterAmountsModalActionActionType.ConfirmationDialogOpened:
            return {
                ...state,
                showConfirmationDialog: true,
                confirmationDialogType: action.dialogType,
                pendingJournalEntryArgs: action.journalEntryArgs,
            };
        case EnterAmountsModalActionActionType.ConfirmationDialogClosed:
            return { ...state, showConfirmationDialog: false, confirmationDialogType: null, isDirty: true, pendingJournalEntryArgs: null };
        case EnterAmountsModalActionActionType.FiltersReset:
            return { ...state, isDirty: false, isFilterReset: true };
        default:
            throw new Error('Unrecognized action type in Enter Amounts Dialog!');
    }
}
